import React from "react";
import Modal from "../Modal";
import { useShowRateBreakdown, type BreakdownRow } from "../../hooks/useShowRateBreakdown";
import type { DateRange } from "../../hooks/useGlobalDateRange";

// =============================================================================
// SHOW RATE BREAKDOWN MODAL (HP-A)
// =============================================================================
// PURPOSE: Drill-down for the Show-up Rate MetricCard on OperationsDashboard.
// Renders the three buckets returned by get_show_rate_breakdown (oid 511226,
// already tie-proven at the SQL layer against get_operations_kpis) — shows,
// no-shows, orphaned pre-calls — each as a count + a compact lead/rep/date row
// list. This component owns ONLY presentation; the fetch lives in
// hooks/useShowRateBreakdown.ts.
// =============================================================================

interface ShowRateBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string | undefined;
  dateRange: DateRange;
  orgTz: string | null | undefined;
}

// Status chip for the Shows bucket — the 5 statuses that make up `shows` per
// the SSOT (PIF + CPP + Lost + Rescheduled + PendingClose). Only text-ds-green
// / text-ds-amber / text-ds-red are semantic colors in this app — no raw
// Tailwind color classes.
const STATUS_CHIP: Record<string, { label: string; className: string }> = {
  paid_in_full: { label: "PIF", className: "bg-ds-green-bg text-ds-green" },
  closed_payment_plan: { label: "CPP", className: "bg-ds-green-bg text-ds-green" },
  lost: { label: "Lost", className: "bg-ds-red-bg text-ds-red" },
  rescheduled: { label: "Resched", className: "bg-ds-amber-bg text-ds-amber" },
  pending_close: { label: "Pending", className: "bg-ds-amber-bg text-ds-amber" },
};

const CANCEL_BY_LABEL: Record<string, string> = {
  Host: "Host cancelled",
  Invitee: "Invitee cancelled",
};

/**
 * `row.date` is already an org-tz-resolved calendar date string (YYYY-MM-DD),
 * per useShowRateBreakdown's BreakdownRow contract. Re-running it through
 * `new Date(...)` + a timezone-aware formatter would re-apply a SECOND tz
 * conversion on top of one the RPC already did — a double-conversion bug for
 * any org whose offset isn't Rayston's. Format the string parts directly
 * instead; this also sidesteps the bare-toLocaleDateString locale gotcha.
 */
function formatCalendarDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "--";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

function StatusChip({ status }: { status: string }) {
  const chip = STATUS_CHIP[status];
  if (!chip) return null;
  return (
    <span className={`token-9 px-1.5 py-0.5 rounded-full shrink-0 ${chip.className}`}>
      {chip.label}
    </span>
  );
}

function CancelByChip({ cancelBy }: { cancelBy: "Host" | "Invitee" | null }) {
  if (!cancelBy) return null;
  const label = CANCEL_BY_LABEL[cancelBy];
  if (!label) return null;
  return (
    <span className="token-9 px-1.5 py-0.5 rounded-full shrink-0 bg-black/[0.05] text-glass-secondary">
      {label}
    </span>
  );
}

function BreakdownRowItem({
  row,
  showStatusChip,
  showCancelByChip,
}: {
  row: BreakdownRow;
  showStatusChip: boolean;
  showCancelByChip: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-black/[0.02] border border-black/[0.05]">
      <div className="flex flex-col min-w-0">
        <span className="token-13 text-glass truncate">{row.lead_name || "Unknown lead"}</span>
        <span className="token-10l text-glass-muted truncate">
          {row.rep_name || "Unattributed"}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {showStatusChip && <StatusChip status={row.status} />}
        {showCancelByChip && <CancelByChip cancelBy={row.cancel_by} />}
        <span className="token-13m text-glass-secondary whitespace-nowrap">
          {formatCalendarDate(row.date)}
        </span>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  count: number;
  countTestId: string;
  countColorClass: string;
  rows: BreakdownRow[];
  showStatusChip?: boolean;
  showCancelByChip?: boolean;
  loading: boolean;
}

function Section({
  title,
  count,
  countTestId,
  countColorClass,
  rows,
  showStatusChip = false,
  showCancelByChip = false,
  loading,
}: SectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className="token-14s text-glass">{title}</span>
        <span data-testid={countTestId} className={`token-24 ${countColorClass}`}>
          {count}
        </span>
      </div>
      {loading ? (
        <div className="token-13 text-glass-muted px-1 py-2">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="token-13 text-glass-muted px-1 py-2">— none —</div>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
          {rows.map((row, i) => (
            <BreakdownRowItem
              key={`${row.lead_id ?? "unknown"}-${row.status}-${row.date}-${i}`}
              row={row}
              showStatusChip={showStatusChip}
              showCancelByChip={showCancelByChip}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ShowRateBreakdownModal: React.FC<ShowRateBreakdownModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  dateRange,
  orgTz,
}) => {
  const { data, loading, error } = useShowRateBreakdown(organizationId, dateRange, orgTz, isOpen);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Show-up Breakdown" maxWidth="max-w-4xl">
      {error ? (
        <div className="token-13 text-ds-red px-1 py-4">{error}</div>
      ) : (
        <div className="flex flex-col gap-6">
          <Section
            title="Shows"
            count={data?.shows_count ?? 0}
            countTestId="breakdown-shows-count"
            countColorClass="text-ds-green"
            rows={data?.shows ?? []}
            showStatusChip
            loading={loading}
          />
          <Section
            title="No-shows"
            count={data?.no_shows_count ?? 0}
            countTestId="breakdown-noshows-count"
            countColorClass="text-ds-amber"
            rows={data?.no_shows ?? []}
            loading={loading}
          />
          <Section
            title="Orphaned pre-calls"
            count={data?.orphans_count ?? 0}
            countTestId="breakdown-orphans-count"
            countColorClass="text-ds-red"
            rows={data?.orphans ?? []}
            showCancelByChip
            loading={loading}
          />
        </div>
      )}
    </Modal>
  );
};

export default ShowRateBreakdownModal;
